import { useState, useEffect, useRef } from 'react';
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  Tag,
  Loader2,
  Check,
  AlertCircle,
  GripVertical,
  FolderOpen,
  Package,
  Copy,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import { cn } from '@/lib/utils';
import { useProducts, type Category } from '@/hooks/useProducts';
import { usePermissions } from '@/hooks/usePermissions';
import { useLocationStore } from '@/store/location';
import { CopyCategoriesModal } from '@/components/products/CopyCategoriesModal';

interface Toast {
  type: 'success' | 'error';
  message: string;
}

export default function Categories() {
  const {
    products,
    categories,
    isLoading,
    locationId,
    createCategory,
    updateCategory,
    deleteCategory,
    reorderCategories,
  } = useProducts();
  const { hasPermission } = usePermissions();
  const { currentLocation } = useLocationStore();

  // Permission checks
  const canEditMenu = hasPermission('menu.edit');
  const canCreateMenu = hasPermission('menu.create');
  const canDeleteMenu = hasPermission('menu.delete');

  const [searchQuery, setSearchQuery] = useState('');
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);

  // Drag and drop state
  const [draggedCategory, setDraggedCategory] = useState<Category | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragNodeRef = useRef<HTMLDivElement | null>(null);

  // Category dialog state
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
  });
  const [categorySaving, setCategorySaving] = useState(false);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Category | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Auto-dismiss toast
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ type, message });
  };

  // Sort categories by sort_order for display (and for drag/drop)
  const sortedCategories = [...(categories || [])].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  // Filter categories by search (use sortedCategories for proper order)
  const filteredCategories = sortedCategories.filter((category) => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        category.name.toLowerCase().includes(query) ||
        category.description?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  // Count products per category
  const getProductCount = (categoryId: string) => {
    return (products || []).filter(p => p.category_id === categoryId).length;
  };

  // Get uncategorized product count
  const uncategorizedCount = (products || []).filter(p => !p.category_id).length;

  // Drag handlers
  const handleDragStart = (e: React.DragEvent, category: Category, _index: number) => {
    setDraggedCategory(category);
    dragNodeRef.current = e.currentTarget as HTMLDivElement;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', category.id);
    // Add a slight delay before applying drag styles
    setTimeout(() => {
      if (dragNodeRef.current) {
        dragNodeRef.current.classList.add('opacity-50');
      }
    }, 0);
  };

  const handleDragEnd = () => {
    if (dragNodeRef.current) {
      dragNodeRef.current.classList.remove('opacity-50');
    }
    setDraggedCategory(null);
    setDragOverIndex(null);
    dragNodeRef.current = null;
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = async (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    if (!draggedCategory || !canEditMenu) return;

    const sourceIndex = sortedCategories.findIndex(c => c.id === draggedCategory.id);
    if (sourceIndex === targetIndex) {
      handleDragEnd();
      return;
    }

    // Create new order
    const newCategories = [...sortedCategories];
    newCategories.splice(sourceIndex, 1);
    newCategories.splice(targetIndex, 0, draggedCategory);

    // Save new order
    try {
      await reorderCategories.mutateAsync(newCategories.map(c => c.id));
      showToast('success', 'Categories reordered');
    } catch (error: any) {
      showToast('error', error.message || 'Failed to reorder categories');
    }

    handleDragEnd();
  };

  // Reset category form
  const resetCategoryForm = () => {
    setCategoryForm({ name: '', description: '' });
    setEditingCategory(null);
  };

  // Open category dialog for creating new category
  const openNewCategory = () => {
    resetCategoryForm();
    setCategoryDialogOpen(true);
  };

  // Open category dialog for editing
  const openEditCategory = (category: Category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      description: category.description || '',
    });
    setCategoryDialogOpen(true);
  };

  // Save category
  const handleSaveCategory = async () => {
    if (!categoryForm.name.trim()) {
      showToast('error', 'Category name is required');
      return;
    }

    if (!locationId) {
      showToast('error', 'No location selected');
      return;
    }

    setCategorySaving(true);
    try {
      const categoryData = {
        location_id: locationId,
        name: categoryForm.name.trim(),
        description: categoryForm.description.trim() || null,
        parent_id: null,
        image_url: null,
        sort_order: editingCategory?.sort_order ?? (categories?.length || 0),
        is_active: true,
      };

      if (editingCategory) {
        await updateCategory.mutateAsync({
          id: editingCategory.id,
          updates: categoryData,
        });
        showToast('success', 'Category updated successfully');
      } else {
        await createCategory.mutateAsync(categoryData);
        showToast('success', 'Category created successfully');
      }

      setCategoryDialogOpen(false);
      resetCategoryForm();
    } catch (error: any) {
      console.error('Error saving category:', error);
      showToast('error', error.message || 'Failed to save category');
    } finally {
      setCategorySaving(false);
    }
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;

    setDeleting(true);
    try {
      await deleteCategory.mutateAsync(deleteTarget.id);
      showToast('success', 'Category deleted successfully');
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    } catch (error: any) {
      showToast('error', error.message || 'Failed to delete category');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      {/* Toast notification */}
      {toast && (
        <div
          className={cn(
            'fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium animate-in slide-in-from-top-2',
            toast.type === 'success' && 'bg-green-500/10 text-green-600 border border-green-500/20',
            toast.type === 'error' && 'bg-red-500/10 text-red-600 border border-red-500/20'
          )}
        >
          {toast.type === 'success' ? (
            <Check className="h-4 w-4" />
          ) : (
            <AlertCircle className="h-4 w-4" />
          )}
          {toast.message}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-blue-500/10">
                <Tag className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{categories?.length || 0}</p>
                <p className="text-xs text-muted-foreground">Total Categories</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-primary/10">
                <Package className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{products?.length || 0}</p>
                <p className="text-xs text-muted-foreground">Total Products</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-amber-500/10">
                <FolderOpen className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{uncategorizedCount}</p>
                <p className="text-xs text-muted-foreground">Uncategorized</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Actions */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search categories..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="flex gap-2 ml-auto">
          {canCreateMenu && categories && categories.length > 0 && (
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => setCopyDialogOpen(true)}
            >
              <Copy className="h-4 w-4" />
              Copy to Locations
            </Button>
          )}
          {canCreateMenu && (
            <Button
              variant="blaze"
              className="gap-2"
              onClick={openNewCategory}
            >
              <Plus className="h-4 w-4" />
              Add Category
            </Button>
          )}
        </div>
      </div>

      {/* Categories List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading categories...</span>
          </div>
        </div>
      ) : !locationId ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
            <AlertCircle className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="font-medium">No location selected</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Please select a location to manage categories
          </p>
        </div>
      ) : filteredCategories.length === 0 && !searchQuery ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
            <Tag className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="font-medium">No categories yet</h3>
          <p className="text-sm text-muted-foreground mt-1 mb-4">
            Create categories to organize your products
          </p>
          {canCreateMenu && (
            <Button
              variant="blaze"
              className="gap-2"
              onClick={openNewCategory}
            >
              <Plus className="h-4 w-4" />
              Add Category
            </Button>
          )}
        </div>
      ) : filteredCategories.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
            <Search className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="font-medium">No categories found</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Try a different search term
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredCategories.map((category, index) => {
            const productCount = getProductCount(category.id);
            const isDraggedOver = dragOverIndex === index && draggedCategory?.id !== category.id;
            return (
              <Card
                key={category.id}
                draggable={canEditMenu && !searchQuery}
                onDragStart={(e) => handleDragStart(e, category, index)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
                className={cn(
                  'hover:shadow-md transition-all',
                  isDraggedOver && 'border-primary border-2 bg-primary/5'
                )}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    {/* Drag Handle */}
                    <div
                      className={cn(
                        'text-muted-foreground/50',
                        canEditMenu && !searchQuery ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'
                      )}
                    >
                      <GripVertical className="h-5 w-5" />
                    </div>

                    {/* Category Icon */}
                    <div className="p-2 rounded-lg bg-blue-500/10">
                      <Tag className="h-5 w-5 text-blue-500" />
                    </div>

                    {/* Category Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold truncate">{category.name}</h3>
                        <Badge variant="secondary" className="text-xs">
                          {productCount} {productCount === 1 ? 'product' : 'products'}
                        </Badge>
                      </div>
                      {category.description && (
                        <p className="text-sm text-muted-foreground truncate mt-0.5">
                          {category.description}
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    {(canEditMenu || canDeleteMenu) && (
                      <div className="flex items-center gap-1">
                        {canEditMenu && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => openEditCategory(category)}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                        )}
                        {canDeleteMenu && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => {
                              setDeleteTarget(category);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {/* Uncategorized Section */}
          {uncategorizedCount > 0 && (
            <Card className="border-dashed">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="w-5" /> {/* Spacer for alignment */}
                  <div className="p-2 rounded-lg bg-muted">
                    <FolderOpen className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-muted-foreground">Uncategorized</h3>
                      <Badge variant="outline" className="text-xs">
                        {uncategorizedCount} {uncategorizedCount === 1 ? 'product' : 'products'}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      Products without a category assigned
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Category Dialog */}
      <ResponsiveModal open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <ResponsiveModalContent maxWidth="sm">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>
              {editingCategory ? 'Edit Category' : 'Add New Category'}
            </ResponsiveModalTitle>
            <ResponsiveModalDescription>
              {editingCategory
                ? 'Update the category details below'
                : 'Create a new category to organize your products'}
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          <ResponsiveModalBody>
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label htmlFor="categoryName">Name *</Label>
                <Input
                  id="categoryName"
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                  placeholder="Category name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="categoryDescription">Description</Label>
                <Textarea
                  id="categoryDescription"
                  value={categoryForm.description}
                  onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                  placeholder="Category description"
                  rows={3}
                />
              </div>
            </div>
          </ResponsiveModalBody>

          <ResponsiveModalFooter className="gap-2">
            {editingCategory && canDeleteMenu && (
              <Button
                variant="destructive"
                onClick={() => {
                  setDeleteTarget(editingCategory);
                  setCategoryDialogOpen(false);
                  setDeleteDialogOpen(true);
                }}
                className="mr-auto"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => {
                setCategoryDialogOpen(false);
                resetCategoryForm();
              }}
            >
              Cancel
            </Button>
            <Button
              variant="blaze"
              onClick={handleSaveCategory}
              disabled={categorySaving}
            >
              {categorySaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {editingCategory ? 'Update Category' : 'Add Category'}
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Delete Confirmation Dialog */}
      <ResponsiveModal open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <ResponsiveModalContent maxWidth="sm">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>Delete Category</ResponsiveModalTitle>
            <ResponsiveModalDescription>
              Are you sure you want to delete &quot;{deleteTarget?.name}&quot;?
              <span className="block mt-2 text-yellow-600">
                {getProductCount(deleteTarget?.id || '')} products will become uncategorized.
              </span>
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          <ResponsiveModalFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setDeleteTarget(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleting}
            >
              {deleting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Delete Category
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Copy Categories to Locations Modal */}
      {locationId && currentLocation && (
        <CopyCategoriesModal
          open={copyDialogOpen}
          onOpenChange={setCopyDialogOpen}
          sourceLocationId={locationId}
          sourceLocationName={currentLocation.name}
          categoryCount={categories?.length}
        />
      )}
    </div>
  );
}

